import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { getProfile, updateProfile } from '../services/api';

function Profile() {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState({
    CustomerName: '',
    CustomerAddress1: '',
    CustomerCity: '',
    CustomerState: '',
    CustomerZip: '',
    CustomerPhone: '',
    CustomerPOC: '',
    podemail: '',
    invemail: '',
    whseemail: '',
    itemail: '',
    uname: '',
    pswd: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const data = await getProfile();
      setProfileData(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage({ type: 'danger', text: 'Error loading profile data' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await updateProfile(profileData);
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (error) {
      console.error('Error saving profile:', error);
      const errorMessage = error.response?.data?.message || 'Error updating profile';
      setMessage({ type: 'danger', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">Loading profile...</div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-0 text-gray-800">Profile - Account Information</h1>
        </Col>
      </Row>

      {message.text && (
        <Row className="mb-3">
          <Col>
            <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
              {message.text}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={8}>
          <Card>
            <Card.Header>
              <h5 className="card-title mb-0">Manage Account Information</h5>
            </Card.Header>
            <Card.Body>
              {/* Company Information Section */}
              <div className="mb-4">
                <h6 className="text-primary mb-3">Company Information</h6>
                
                <Form.Group className="mb-3">
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="CustomerName"
                    value={profileData.CustomerName}
                    readOnly
                    className="form-control-plaintext"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address Line 1</Form.Label>
                  <Form.Control
                    type="text"
                    name="CustomerAddress1"
                    value={profileData.CustomerAddress1}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        name="CustomerCity"
                        value={profileData.CustomerCity}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        type="text"
                        name="CustomerState"
                        value={profileData.CustomerState}
                        onChange={handleInputChange}
                        maxLength={5}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Zip Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="CustomerZip"
                        value={profileData.CustomerZip}
                        onChange={handleInputChange}
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Point of Contact (POC)</Form.Label>
                      <Form.Control
                        type="text"
                        name="CustomerPOC"
                        value={profileData.CustomerPOC}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="CustomerPhone"
                        value={profileData.CustomerPhone}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Email Information Section */}
              <div className="mb-4">
                <h6 className="text-primary mb-3">Email Notifications</h6>
                <p className="text-muted small mb-3">
                  Enter the email addresses that you would like to receive notifications for each type of document.<br />
                  Separate email addresses with ";" or ",".
                </p>

                <Form.Group className="mb-3">
                  <Form.Label>BOL Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="podemail"
                    value={profileData.podemail}
                    onChange={handleInputChange}
                    placeholder="Enter email addresses for BOL notifications"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Warehouse Receipt Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="whseemail"
                    value={profileData.whseemail}
                    onChange={handleInputChange}
                    placeholder="Enter email addresses for warehouse receipt notifications"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Invoice Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="invemail"
                    value={profileData.invemail}
                    onChange={handleInputChange}
                    placeholder="Enter email addresses for invoice notifications"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Inventory Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="itemail"
                    value={profileData.itemail}
                    onChange={handleInputChange}
                    placeholder="Enter email addresses for inventory notifications"
                  />
                </Form.Group>
              </div>

              {/* Web Access Section */}
              <div className="mb-4">
                <h6 className="text-primary mb-3">Web Access</h6>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        name="uname"
                        value={profileData.uname}
                        readOnly
                        className="form-control-plaintext"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="pswd"
                        value={profileData.pswd}
                        onChange={handleInputChange}
                        placeholder="Enter new password to change"
                      />
                      <Form.Text className="text-muted">
                        Leave blank to keep current password
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Card.Body>
            <Card.Footer>
              <Button 
                variant="primary" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Profile; 